import React from 'react';
import { ScopedCssBaseline, ThemeProvider } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';

import themeAssure from './theme';

export const ETAPES = [
  { cle: 'sinistre', titre: 'Votre sinistre' },
  { cle: 'contrat', titre: 'Votre contrat' },
  { cle: 'intervenants', titre: 'Les intervenants' },
  { cle: 'logement', titre: 'Votre logement' },
  { cle: 'dommages', titre: 'Vos dommages' },
  { cle: 'contenu', titre: 'Votre contenu' },
  { cle: 'chiffrage', titre: 'Votre chiffrage' },
  { cle: 'frais', titre: 'Vos autres frais' },
  { cle: 'dossier', titre: 'Votre dossier' },
  { cle: 'reponse', titre: "La réponse de l'assureur" },
];

/**
 * Coquille du parcours assuré.
 *
 * `ScopedCssBaseline` isole ces écrans du CSS Bootstrap importé globalement
 * par le front expert : les deux cohabitent sans se déformer, jusqu'à ce que
 * l'ancien front soit repris.
 */
export default function Layout({ etape, titre, introduction, children, actions,
                                 onEtape }) {
  const index = ETAPES.findIndex((e) => e.cle === etape);
  // L'aide se demande là où l'on cale, pas en revenant à l'accueil : le lien
  // suit donc chaque étape et emporte le dossier avec lui.
  const aide = onEtape && etape && !['aide', 'assistance'].includes(etape);

  return (
    <ThemeProvider theme={themeAssure}>
      <ScopedCssBaseline>
        {/* À l'impression, le dossier doit tenir seul : le fil d'étapes, les
            boutons et les avertissements d'écran n'ont rien à y faire. */}
        <style>{`
          @media print {
            .sans-impression, .MuiStepper-root, button { display: none !important; }
            body { background: #fff; }
          }
        `}</style>
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: { xs: 3, md: 5 } }}>
          <Container maxWidth="md">
            {index >= 0 && (
              <Stepper activeStep={index} alternativeLabel nonLinear sx={{ mb: 5 }}>
                {ETAPES.map((e) => (
                  <Step key={e.cle} completed={false}>
                    {/* Le parcours se lit dans l'ordre mais ne s'y enferme
                        pas : on revient corriger une étape sans tout refaire. */}
                    {onEtape ? (
                      <StepButton onClick={() => onEtape(e.cle)}>
                        {e.titre}
                      </StepButton>
                    ) : (
                      <StepLabel>{e.titre}</StepLabel>
                    )}
                  </Step>
                ))}
              </Stepper>
            )}

            {titre && (
              <Typography variant="h1" sx={{ mb: introduction ? 1.5 : 4 }}>
                {titre}
              </Typography>
            )}
            {introduction && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: '58ch' }}>
                {introduction}
              </Typography>
            )}

            {children}

            {aide && (
              <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid',
                         borderColor: 'divider' }}
                   className="sans-impression">
                <Typography variant="body2" color="text.secondary"
                            sx={{ mb: 1 }}>
                  Une question sur cette étape, ou besoin d'être accompagné ?
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined"
                          onClick={() => onEtape('aide')}>
                    J'ai une question
                  </Button>
                  <Button size="small" variant="outlined"
                          onClick={() => onEtape('assistance')}>
                    C'est trop compliqué, aidez-moi
                  </Button>
                </Box>
              </Box>
            )}

            {actions && (
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 5 }}>
                {actions}
              </Box>
            )}
          </Container>
        </Box>
      </ScopedCssBaseline>
    </ThemeProvider>
  );
}
